import { createFileRoute, useLocation } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import Logo from '@/assets/icons/logo.svg?react';
import { CometlyPixel } from '@/components/CometlyPixel';
import { getEnvVar } from '@/env';
import {
  buildDesktopCallbackUrl,
  exchangeCodeForTokens as exchangeCodeForTokensUtil,
  readAfterRedirect,
  readEmailFromToken,
  waitForLoginBrowserAttribution,
  type TokenExchangeResponse,
} from '@/features/login';
import { recordGoogleSignupIntent } from '@/features/login/google-signup-consent-config';
import { setLastLoginEmail } from '@/utils/localStorage';

const redirectParamsSchema = z.object({
  code: z.string().catch(''),
  state: z.string().catch(''),
  error: z.string().optional(),
  error_description: z.string().optional(),
});

type LocationState = {
  tokens?: TokenExchangeResponse;
  deepLinkProtocol?: string;
  loginAttempt?: string;
};

const auth0Config = {
  domain: getEnvVar('VITE_AUTH0_DOMAIN'),
  clientId: getEnvVar('VITE_AUTH0_CLIENT_ID'),
  audience: getEnvVar('VITE_AUTH0_AUDIENCE'),
};

async function exchangeCodeForTokens(
  code: string,
  _state: string,
  codeVerifier: string | null
): Promise<TokenExchangeResponse> {
  const redirectUri = `${getEnvVar('BASE_URL_FOR_PUBLIC_LINKS')}/auth-callback/desktop`;

  const params: Record<string, string> = {
    grant_type: 'authorization_code',
    code,
    client_id: auth0Config.clientId,
    redirect_uri: redirectUri,
    audience: auth0Config.audience,
  };

  if (codeVerifier) {
    params.code_verifier = codeVerifier;
  }

  return exchangeCodeForTokensUtil(auth0Config, params);
}

export const Route = createFileRoute('/auth-callback/desktop')({
  component: RouteComponent,
  validateSearch: (search) => redirectParamsSchema.parse(search),
});

function RouteComponent() {
  const params = Route.useSearch();
  const location = useLocation();
  const isPromptTriggered = useRef(false);
  const completedSignIn = useRef<{
    tokenResponse?: TokenExchangeResponse;
    loginAttempt?: string;
  }>({});
  const [isProcessing, setIsProcessing] = useState(false);

  const openLittleBird = useCallback(async () => {
    const { tokenResponse, loginAttempt } = completedSignIn.current;

    const locationState = location.state as LocationState | undefined;
    const deepLinkProtocol = locationState?.deepLinkProtocol;
    const loginBrowserAttribution = tokenResponse
      ? await waitForLoginBrowserAttribution()
      : undefined;
    const deepLinkUrl = buildDesktopCallbackUrl({
      params,
      tokenResponse,
      deepLinkProtocol,
      loginBrowserAttribution,
      loginAttempt,
    });
    window.location.href = deepLinkUrl;
  }, [params, location.state]);

  const handleOAuthCallback = useCallback(
    async (
      authCode: string,
      stateParam: string,
      stored: ReturnType<typeof readAfterRedirect>
    ) => {
      if (isProcessing) return;
      setIsProcessing(true);

      try {
        const tokenResponse = await exchangeCodeForTokens(
          authCode,
          stateParam,
          stored.codeVerifier
        );
        const signedInEmail = tokenResponse.id_token
          ? readEmailFromToken(tokenResponse.id_token)
          : null;
        if (signedInEmail) setLastLoginEmail(signedInEmail);
        await recordGoogleSignupIntent(
          tokenResponse.access_token,
          stored.googleSignupToolkits
        );
        completedSignIn.current = {
          tokenResponse,
          loginAttempt: stored.loginAttempt ?? undefined,
        };
        sessionStorage.removeItem(stateParam);
        await openLittleBird();
      } catch (error: unknown) {
        console.error('Error exchanging code for tokens:', error);
        toast.error('Authentication failed. Please try again.');
        setIsProcessing(false);
      }
    },
    [isProcessing, openLittleBird]
  );

  useEffect(() => {
    if (isPromptTriggered.current) return;
    isPromptTriggered.current = true;

    const locationState = location.state as LocationState | undefined;

    if (locationState?.tokens) {
      completedSignIn.current = {
        tokenResponse: locationState.tokens,
        loginAttempt: locationState.loginAttempt,
      };
      void openLittleBird();
      return;
    }

    if (params.code && params.state) {
      const stored = readAfterRedirect(params.state);
      if (stored.codeVerifier) {
        void handleOAuthCallback(params.code, params.state, stored);
        return;
      }
      void openLittleBird();
      return;
    }

    if (params.error) {
      toast.error(params.error_description || params.error);
      return;
    }

    void openLittleBird();
  }, [params, location.state, handleOAuthCallback, openLittleBird]);

  return (
    <>
      <CometlyPixel />
      <div className="flex fixed inset-0 w-screen min-h-full items-center justify-center p-4 bg-background text-foreground">
        <div className="flex flex-col gap-2 justify-center align-middle">
          <Logo aria-label="Littlebird" className="mx-auto size-10" />
          <p className="text-xl">Redirecting to your Littlebird app...</p>
          <a
            href="#"
            className="underline text-foreground-subtle text-center"
            onClick={(e) => {
              e.preventDefault();
              void openLittleBird();
            }}
          >
            If you weren't redirected, click here
          </a>
        </div>
      </div>
    </>
  );
}
